import React from 'react';
import {Container} from '@inlet/react-pixi';
import {ReactMediator, FullScreenFader, IReactView} from '@gongxigames/framework/core/fragments/react';
import {Container as PixiContainer} from 'pixi.js';
import {ReactPIXISystemPopup, SystemPopupPropsTypes} from '@gongxigames/framework/slot/types';
import {CoreDI, lazyInject, ScreenParameters, setLayer, System} from '@gongxigames/framework/core';
import {ISystemPopupView} from './index';
import {LAYERS} from '@gongxigames/framework/slot/configs/Layers';
import {slotStore} from '@gongxigames/framework/slotV2/globals';

type SystemPopupsViewState = {
    visible: boolean;
    popup?: ReactPIXISystemPopup;
    screenParameters?: ScreenParameters;
};

const REACT_POPUP_TYPE = 'ReactPIXI';

export class ReactSystemPopupsView implements ISystemPopupView, IReactView {

    @lazyInject(CoreDI.System)
    protected _system!: System;

    public state: SystemPopupsViewState = {
        visible: false
    };

    protected _reactMediator!: ReactMediator;

    protected _container: PixiContainer = new PixiContainer();
    protected onClosePopupCallback: (id: string) => void = () => {};

    public init(screenParameters: ScreenParameters): void {
        this.createReactMediator();

        this._container.name = 'ReactSystemPopupsView';

        if (slotStore.slot_isGameStateMachineActive.value && slotStore.stage_rootContainer_ui.value) {
            slotStore.stage_rootContainer_ui.value.addChild(this.container);
            this.container.scale.set(0.5);
        } else {
            this._system.app.stageV1.addChild(this.container);
        }

        this.setState({screenParameters});

        setLayer(LAYERS.SYSTEM_POPUPS, this._container);
    }

    public get container(): PixiContainer {
        return this._container;
    }

    protected createReactMediator(): void {
        this._reactMediator = ReactMediator.fromReactView(this);
    }

    public destroy(): void {
        this.container.parent?.removeChild(this.container);
        ReactMediator.unmountComponent(this.container);
    }

    protected forceUpdate(): void {
        this._reactMediator.forceUpdate();
    }

    protected setState<K extends keyof any>(
        state: ((prevState: Readonly<any>, props: Readonly<any>) => (Pick<any, K> | any | null)) | Pick<any, K> | any | null,
        callback?: () => void
    ): void {
        this._reactMediator.setState(state, callback);
    }

    public show(popup: ReactPIXISystemPopup): void {
        if (popup.type !== REACT_POPUP_TYPE) {
            return;
        }

        this.setState({visible: true, popup});
    }

    public hide(popup: ReactPIXISystemPopup): void {
        if (popup.type !== REACT_POPUP_TYPE) {
            return;
        }

        if (this.state.popup && this.state.popup.id === popup.id) {
            this.hideCurrentPopup();
        }
    }

    public hideCurrentPopup(): void {
        this.setState({visible: false, popup: undefined});
    }

    public resize(screenParameters: ScreenParameters): void {
        this.setState({screenParameters});
        this._reactMediator.forceUpdate();
    }

    public render(): JSX.Element {
        const {visible, screenParameters} = this.state;

        if (!screenParameters) {
            return <></>;
        }

        return (
            <Container visible={visible}>
                <FullScreenFader screenParameters={screenParameters} alpha={0.82} interactive={true}/>
                {this.getPopup()}
            </Container>
        );
    }

    protected getPopup(): any {
        const {popup} = this.state;

        if (popup) {
            const props: SystemPopupPropsTypes<any> = {
                id: popup.id,
                data: popup.data,
                onClose: this.onClosePopupCallback
            };

            return React.createElement<SystemPopupPropsTypes<any>>(popup.view, props);
        }

        return null;
    }
}
